import { useState, useEffect, useCallback } from 'react';
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight, CarFront } from 'lucide-react';
import { ExportCSVButton, ExportPDFButton } from '../components/ExportButtons';
import { getPlayersReport, listAllGolfCourses, listSettings } from '../api';
import { fmtReportDate, exportCSV, fmtCSVDate, fmtCSVFilename } from '../utils/reportExport';
import PrintBanner from '../components/PrintBanner/PrintBanner';
import ReportFilterBar from '../components/ReportFilterBar';
import styles from './PlayersReportPage.module.css';
import HolePill from '../components/HolePill';
import PlayerTypePill from '../components/PlayerTypePill';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function fmtTime(t) {
  if (!t) return '—';
  const match = t.match(/(\d{1,2}):(\d{2})/);
  if (!match) return t;
  let h = parseInt(match[1], 10);
  const m = match[2];
  const ap = h < 12 ? 'AM' : 'PM';
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${m} ${ap}`;
}

function addDaysISO(iso, n) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().split('T')[0];
}

function dateRange(from, to) {
  const dates = [];
  let cur = from;
  while (cur <= to) {
    dates.push(cur);
    cur = addDaysISO(cur, 1);
  }
  return dates;
}

function formatLongDate(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });
}

const SORTABLE_COLS = [
  { key: 'booking_number', label: 'Booking #' },
  { key: 'date',           label: 'Date' },
  { key: 'time',           label: 'Time' },
  { key: 'golf_course_name', label: 'Golf course' },
  { key: 'hole',           label: 'Hole', sortable: false },
  { key: 'name',           label: 'Name', sortable: false },
  { key: 'type',           label: 'Type', sortable: false },
  { key: 'needs_cart',     label: 'Needs cart?', sortable: false },
  { key: 'holes_to_play',  label: 'Holes to play' },
  { key: 'status',         label: 'Status' },
];

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function PlayersReportPage() {
  const today = todayISO();
  const [filters, setFilters] = useState({
    bookingNumber: '',
    dateFrom: today,
    dateTo: today,
    courseId: '',
    hole: '',
    name: '',
    status: '',
    playerType: '',
  });
  const [applied, setApplied]     = useState(null);
  const [courses, setCourses]     = useState([]);
  const [sortBy, setSortBy]       = useState('date');
  const [sortDir, setSortDir]     = useState('asc');
  const [items, setItems]         = useState([]);
  const [total, setTotal]         = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [dates, setDates]         = useState([]);
  const [dayIdx, setDayIdx]       = useState(0);
  const [companyInfo, setCompanyInfo] = useState({ name: '', address: '', website: '', logo: '' });

  useEffect(() => {
    listAllGolfCourses()
      .then(r => setCourses(r.items || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    listSettings()
      .then(data => {
        const map = {};
        (data.settings || data.items || []).forEach(s => { map[s.key] = s.value; });
        setCompanyInfo({ name: map.company_name || '', address: map.company_address || '', website: map.company_website || '', logo: map.company_logo || '' });
      })
      .catch(() => {});
  }, []);

  const fetchPage = useCallback((params) => {
    setLoading(true);
    setItems([]);
    setTotal(null);
    setError(null);
    getPlayersReport(params)
      .then(d => {
        setItems(d.items || []);
        setTotal(d.total ?? null);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  // When sort changes, refetch from the top
  const handleSort = useCallback((col) => {
    if (!applied) return;
    const colDef = SORTABLE_COLS.find(c => c.key === col);
    if (colDef?.sortable === false) return;
    const newDir = sortBy === col ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc';
    setSortBy(col);
    setSortDir(newDir);
    fetchPage({ ...applied, sortBy: col, sortDir: newDir });
  }, [applied, sortBy, sortDir, fetchPage]);

  const handleApply = () => {
    const params = {
      bookingNumber: filters.bookingNumber || undefined,
      dateFrom: filters.dateFrom || undefined,
      dateTo: filters.dateTo || undefined,
      courseId: filters.courseId || undefined,
      hole: filters.hole || undefined,
      name: filters.name || undefined,
      status: filters.status || undefined,
      playerType: filters.playerType || undefined,
      sortBy,
      sortDir,
    };
    setApplied(params);
    setDates(dateRange(filters.dateFrom || todayISO(), filters.dateTo || todayISO()));
    setDayIdx(0);
    fetchPage(params);
  };

  const handleClear = () => {
    setFilters({ bookingNumber: '', dateFrom: today, dateTo: today, courseId: '', hole: '', name: '', status: '', playerType: '' });
    setApplied(null);
    setItems([]);
    setTotal(null);
    setError(null);
    setDates([]);
    setDayIdx(0);
  };

  const handleExportCSV = () => {
    if (!items.length) return;
    const headers = ['Booking #', 'Date', 'Time', 'Golf Course', 'Hole', 'Name', 'Member #', 'Type', 'Needs Cart?', 'Holes to Play', 'Status'];
    const rows = items.map(p => [
      p.booking_number || '',
      p.tee_time ? fmtCSVDate(p.tee_time.split('T')[0]) : '',
      p.tee_time ? fmtTime(p.tee_time) : '',
      p.golf_course || '',
      p.hole != null ? `Hole ${p.hole}` : '',
      p.name || '',
      p.member_number || '',
      p.is_member ? 'Member' : 'Guest',
      p.needs_golf_cart ? 'Yes' : 'No',
      p.holes_to_play != null ? String(p.holes_to_play) : '',
      p.status || '',
    ]);
    const from = applied?.dateFrom || '';
    const to = applied?.dateTo || '';
    exportCSV(headers, rows, fmtCSVFilename('player_list', from, to));
  };

  const currentDate = dates[dayIdx] || null;
  const dayItems = currentDate
    ? items.filter(p => p.tee_time && p.tee_time.split('T')[0] === currentDate)
    : items;

  function SortIcon({ col }) {
    const colDef = SORTABLE_COLS.find(c => c.key === col);
    if (colDef?.sortable === false) return null;
    if (sortBy !== col) return null;
    return sortDir === 'asc'
      ? <ChevronUp size={12} strokeWidth={2} />
      : <ChevronDown size={12} strokeWidth={2} />;
  }

  return (
    <div className={styles.page}>
      {/* Toolbar + filters — hidden when printing */}
      <div className="no-print">
        <ReportFilterBar
          title="Player list"
          dateFrom={filters.dateFrom}
          dateTo={filters.dateTo}
          onDateFromChange={v => setFilters(f => ({ ...f, dateFrom: v }))}
          onDateToChange={v => setFilters(f => ({ ...f, dateTo: v }))}
          dateFromLabel="Start date"
          dateToLabel="End date"
          courseId={filters.courseId}
          courses={courses}
          onCourseChange={v => setFilters(f => ({ ...f, courseId: v }))}
          onApply={handleApply}
          onClear={handleClear}
          extraFilters={
            <>
              <label className={styles.label}>Hole</label>
              <select
                className={styles.select}
                value={filters.hole}
                onChange={e => setFilters(f => ({ ...f, hole: e.target.value }))}
              >
                <option value="">All holes</option>
                <option value="1">Hole 1</option>
                <option value="10">Hole 10</option>
              </select>

              <label className={styles.label}>Booking #</label>
              <input
                className={styles.input}
                style={{ width: 90 }}
                placeholder="BK-00000"
                value={filters.bookingNumber}
                onChange={e => setFilters(f => ({ ...f, bookingNumber: e.target.value }))}
              />

              <label className={styles.label}>Name</label>
              <input
                className={styles.input}
                style={{ width: 160 }}
                placeholder="Search name…"
                value={filters.name}
                onChange={e => setFilters(f => ({ ...f, name: e.target.value }))}
              />

              <label className={styles.label}>Status</label>
              <select
                className={styles.select}
                value={filters.status}
                onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
              >
                <option value="">All statuses</option>
                <option value="booked">Booked</option>
                <option value="paid">Paid</option>
                <option value="no_show">No show</option>
              </select>

              <label className={styles.label}>Type</label>
              <select
                className={styles.select}
                value={filters.playerType}
                onChange={e => setFilters(f => ({ ...f, playerType: e.target.value }))}
              >
                <option value="">All types</option>
                <option value="member">Member</option>
                <option value="guest">Guest</option>
              </select>
            </>
          }
        >
          {items.length > 0 && (
            <>
              <ExportCSVButton onClick={handleExportCSV} />
              <ExportPDFButton onClick={() => window.print()} />
            </>
          )}
        </ReportFilterBar>
      </div>{/* end no-print */}

      {/* Day navigation strip — hidden when printing */}
      {dates.length > 0 && items.length > 0 && (
        <div className={`${styles.dayNav} no-print`}>
          <div className={styles.dayNavCenter}>
            <button
              className={styles.navBtn}
              onClick={() => setDayIdx(i => i - 1)}
              disabled={dayIdx === 0}
              aria-label="Previous day"
            >
              <ChevronLeft size={16} strokeWidth={1.5} />
            </button>
            <div>
              <div className={styles.dayNavDate}>{dates[dayIdx] ? formatLongDate(dates[dayIdx]) : '—'}</div>
              <div className={styles.dayNavCounter}>Day {dayIdx + 1} of {dates.length}</div>
            </div>
            <button
              className={styles.navBtn}
              onClick={() => setDayIdx(i => i + 1)}
              disabled={dayIdx === dates.length - 1}
              aria-label="Next day"
            >
              <ChevronRight size={16} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className={styles.body}>
        <PrintBanner
          companyInfo={companyInfo}
          reportTitle="Player List"
          dateRange={applied?.dateFrom && applied?.dateTo ? `${fmtReportDate(applied.dateFrom)} – ${fmtReportDate(applied.dateTo)}` : undefined}
          course={applied?.courseId ? (courses.find(c => String(c.id) === String(applied.courseId))?.name || 'All courses') : 'All courses'}
          generatedOn={new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        />
        {error && <div className={styles.errorState}>Failed to load data. Please try again.</div>}
        {loading && <div className={styles.emptyState}>Loading...</div>}
        {!loading && !error && items.length === 0 && (
          <div className={styles.emptyState}>
            {applied ? 'No players match the selected filters.' : 'Set filters and click Apply to search players.'}
          </div>
        )}
        {!loading && items.length > 0 && dayItems.length === 0 && (
          <div className={styles.emptyState}>No players on this day.</div>
        )}
        {!loading && dayItems.length > 0 && (
          <table className={styles.table}>
            <thead>
              <tr>
                {SORTABLE_COLS.map(col => {
                  const sortable = col.sortable !== false;
                  return (
                    <th
                      key={col.key}
                      className={sortable ? styles.sortable : ''}
                      onClick={sortable ? () => handleSort(col.key) : undefined}
                    >
                      {col.label}
                      {sortable && <SortIcon col={col.key} />}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {dayItems.map((p, i) => (
                <tr key={i}>
                  <td className={styles.mono}>{p.booking_number}</td>
                  <td>{fmtDate(p.tee_time ? p.tee_time.split('T')[0] : null)}</td>
                  <td>{fmtTime(p.tee_time)}</td>
                  <td className={styles.muted}>{p.golf_course || '—'}</td>
                  <td>
                    <HolePill hole={p.hole} />
                  </td>
                  <td>
                    <div className={styles.playerName}>{p.name}</div>
                    {p.is_member && p.member_number && (
                      <div className={styles.memberNum}>{p.member_number}</div>
                    )}
                  </td>
                  <td>
                    <PlayerTypePill isMember={p.is_member} />
                  </td>
                  <td>
                    {p.needs_cart ? (
                      <span className={styles.cartYes}>
                        <CarFront size={12} strokeWidth={1.5} />
                        Yes
                      </span>
                    ) : (
                      <span className={styles.cartNo}>No</span>
                    )}
                  </td>
                  <td className={styles.muted}>{p.holes_to_play || '—'}</td>
                  <td>
                    <span className={
                      p.status === 'Paid' ? styles.statusPaid
                        : p.status === 'No Show' ? styles.statusNoShow
                        : styles.statusBooked
                    }>
                      {p.status || '—'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
